<template>
  <div>
    <!--begin::Portlet-->
    <div class="kt-portlet">
      <div class="kt-portlet__body">
        <div class="row">
          <div class="col-6">
            Selection View
            <button class="btn btn-ss ml-2"
                    :disabled="view === 'bars'" @click="changeListView('bars')">
              <i class="fa fa-signal" />
            </button>
            <button class="btn btn-ss"
                    :disabled="view === 'list'" @click="changeListView('list')">
              <i class="la la-list" />
            </button>
          </div>
          <div class="col-6 text-right">
            <div class="input-group">
              <input id="silence" v-model="silence"
                     v-validate="'is_time'"
                     v-mask="'##:##:##'"
                     date-vv-scope="silence" name="silence"
                     type="text"
                     class="form-control time kt-input" placeholder="00:00:00">
              <div class="input-group-append">
                <button type="button" class="btn btn-ss"
                        @click="addSilence">
                  <i class="la la-plus" />Add Silence
                </button>
              </div>
            </div>
            <p v-show="errors.has('silence')" class="text-ss-danger text-left">
              {{ errors.first('silence') }}
            </p>
          </div>
        </div>
        <div class="view-toggle" />

        <div v-if="!selected_assets.length">
          <div class="stitcher-placeholder">
            <p>No files selected. Please choose files below.</p>
          </div>
        </div>

        <div v-if="selected_assets.length && view === 'list'">
          <!-- List View -->
          <SlickList v-model="selected_assets" class="list"
                     :lock-to-container-edges="true" :use-drag-handle="true"
                     lock-axis="y">
            <SlickItem v-for="(selected_asset, index) in selected_assets" :key="index"
                       class="list-item row" :index="index">
              <div class="col-1">
                <span v-handle class="handle" />
              </div>
              <div class="col-5">
                <span>{{ selected_asset.name }}</span>
              </div>
              <div class="col-4">
                <span>{{ selected_asset.file_data }}</span>
              </div>
              <div class="col-1">
                <a v-if="!selected_asset.silence"
                   :href="`/storage/${selected_asset.uuid}/edit`"
                   class="">Edit Asset</a>
              </div>
              <div class="col-1">
                <button type="button" class="btn btn-sm btn-ss-danger"
                        @click="() => removeItem(selected_asset)">
                  X
                </button>
              </div>
            </SlickItem>
          </SlickList>
        <!-- / List View -->
        </div>

        <div v-if="selected_assets.length && view === 'waveforms'">
          <!-- Waveform View -->
          <SlickList v-model="selected_assets"
                     class="list row horizontal-list" :lock-to-container-edges="true"
                     axis="x">
            <SlickItem v-for="(selected_asset, index) in selected_assets" :key="index"
                       class="horizontal-list-item" :style="{ width: itemWidthPercentage(index) + '%' }"
                       :index="index">
              <div class="item-title">
                {{ selected_asset.name }}
              </div>
              <simple-waveform v-if="!selected_asset.silence"
                               :player-id="'item-' + index" :file-path="selected_asset.file_path"
                               :waveform="selected_asset.waveform" />
            </SlickItem>
          </SlickList>
        <!-- Waveform View -->
        </div>

        <div v-if="selected_assets.length && view === 'bars'">
          <!-- Bars View -->
          <SlickList v-model="selected_assets"
                     class="list row horizontal-list"
                     :use-drag-handle="true"
                     :lock-to-container-edges="true"
                     axis="x">
            <SlickItem v-for="(selected_asset, index) in selected_assets" :key="index"
                       class="horizontal-list-item"
                       :style="{ width: itemWidthPercentage(index) + '%' }" :index="index">
              <div class="bar-actions">
                <button v-handle href="javascript:;" class="handle btn btn-sm btn-secondary">
                  <i class="la la-arrows-h" />
                </button>
                <button href="javascript:;" class="handle btn btn-sm btn-secondary" @click="removeItem(selected_asset)">
                  <i class="la la-close" />
                </button>
              </div>

              <div class="item-title">
                {{ selected_asset.name }}
              </div>
              <div v-if="! selected_asset.silence" :id="'bars-item-' + index" class="bar" />
              <div v-else :id="'bars-item-' + index" class="bar silence" />
            </SlickItem>
          </SlickList>
        <!-- Bars View -->
        </div>

        <div v-if="incompatibility_issues.length" class="alert alert-ss-danger fade show" role="alert">
          <div class="alert-icon">
            <i class="flaticon-warning" />
          </div>
          <div class="alert-text">
            <strong>Incompatibility Issue : </strong><br>
            <p v-for="(incompatibility_issue, index) in incompatibility_issues" :key="index">
              {{ incompatibility_issue }}
            </p>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <div class="form-group kt-form__group">
              <label for="stitch-name">Name your new file</label>
              <input id="stitch-name" v-model="stitch_name"
                     v-validate.continues="'required|without_file_extension|alpha_dash'"
                     type="text"
                     data-vv-scope="file-name"
                     name="New Stitch Name" class="form-control kt-input"
                     placeholder="my-new-file">
              <span class="kt-form__help">Enter the name of your
                stitched file
                without file extension (i.e. mp3)</span>

              <span v-show="errors.has('New Stitch Name', 'file-name')" class="text-ss-danger"><br>{{ errors.first('New Stitch Name', 'file-name') }}</span>
            </div>
          </div>
          <div class="col-6">
            <div class="form-group kt-form__group">
              <label class="is-invisible">&nbsp;</label><br>
              <button class="btn btn-ss-standout"
                      :disabled="selected_assets.length === 0 || !stitch_name" @click="stitch()">
                <span>
                  <i class="la la-scissors" />
                  <span>Stitch Files</span>
                </span>
              </button>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 mb-2">
            <hr>
          </div>
        </div>
        <f-data-table :id="tbl_conf.id"
                      :data-uri="tbl_conf.data_uri"
                      :server-side="tbl_conf.server_side"
                      :columns="tbl_conf.columns"
                      heading="Storage"
                      icon="flaticon2-download"
                      :expanded="tbl_conf.expanded"
                      :locked="tbl_conf.expanded">
          <template #_actions="props">
            <b-button variant="ss" @click="props.reloadData">
              <i class="flaticon2-refresh" />Reload Data
            </b-button>
          </template>
          <template #_filters>
            <f-data-table-filters :target="tbl_conf.id"
                                  :expanded="false"
                                  :filters="tbl_conf.filters" />
          </template>
          <!-- COLUMNS -->
          <template #actions="props">
            <button class="btn btn-sm btn-ss" type="button" @click="() => addAsset(props.row)">
              + Add
            </button>
          </template>
        </f-data-table>
      </div>
    </div>
    <!--end::Portlet-->
  </div>
</template>
<script>
import "vue-multiselect/dist/vue-multiselect.min.css";
import { SlickList, SlickItem, HandleDirective } from "vue-slicksort";
import VeeValidate from "vee-validate";
import VueTheMask from "vue-the-mask";
import SimpleWaveform from "VuePartials/SimpleWaveform";

import FDataTable from "VuePartials/App/Storage/FDataTable/FDataTable.vue";
import FDataTableFilters from "VuePartials/App/Storage/FDataTable/FDataTableFilters.vue";

Vue.use(VueTheMask);
Vue.use(VeeValidate);

export default {
  directives: { handle: HandleDirective },
  components: {
    SlickItem,
    SlickList,
    SimpleWaveform,
    FDataTable,
    FDataTableFilters,
  },
  data() {
    return {
      view: "bars",
      silence: "00:00:00",
      silenceId: 0,
      selected_assets: [],
      selected_assets_sorted: [],
      incompatibility_issues: [],
      bit_rate_incompatible: false,
      format_incompatible: false,
      stitch_name: null,
      tbl_conf: {
        id: 'storage-table',
        server_side: true,
        data_uri: `/api/v1/storage`,
        columns: [
          {
            display: `Name`,
            field: `name`,
            clip_resistance: 1,
          },
          {
            display: `Bitrate`,
            sortable: false, // cuz server-side can't do nested key sorting
            renderHtml: row => row.ffmpeg_details?.bitrate,
          },
          {
            display: "Updated At",
            field: "updated_at",
            default_sort: true,
          },
          {
            field: `actions`,
            display: `Actions`,
            sortable: false,
            width: 150,
            never_clip: true,
          },
        ],
        filters: [
          {
            name: `name`,
            display: `Name`,
            type: `text`,
          },
        ],
        expanded: true,
      },
    };
  },
  computed: {
    max_bitrate_diff() {
      return 1000;
    },
    calculatedItemRatios() {
      const total = this.totalDuration; // this is 100%
      const calculated_items = [];
      let minimum_offset = 0;
      let minimum_offset_count = 0;
      const minimum_percent = 10;

      _.forEach(this.selected_assets, (item, index) => {
        // Turn the total duration into 100%
        let percent =
                    (parseInt(item.ffmpeg_details.duration) * 100) / total;

        // Check to see if the file in this iteration accounts for less than the minimum width we allow
        if (percent < minimum_percent) {
          // Maintain a count of the items that have been changed to the minimum width
          minimum_offset_count++;
          // Maintain a record of the amount we have added to this percentage to take it up to the minimum
          minimum_offset += minimum_percent - percent;
          // Set the width to be the minimum we allow
          percent = minimum_percent;
        }

        // Add the item we have calculated to the object array
        calculated_items.push({ index: index, percent: percent });
      });

      // Now we have adjusted, we now need to loop again and re-adjust the percentages since we have added to the minimums
      _.forEach(calculated_items, (data, index) => {
        // If percentage of this iteration is the minimum we know it's been changed by us
        if (data.percent !== minimum_percent && minimum_offset_count) {
          // Based on the count of items we have changed to the minimum calculate what we need to distribute for each item
          const total_above_minimum =
                        calculated_items.length - minimum_offset_count;
          const amount_to_offset = minimum_offset / total_above_minimum;
          calculated_items[index].percent =
                        calculated_items[index].percent - amount_to_offset;
        }
      });
      return calculated_items;
    },
    totalDuration() {
      return this.selected_assets.reduce((acc, item) => acc + parseFloat(item.ffmpeg_details.duration), 0);
    },
  },
  methods: {
    getFileExtension(file_name) {
      const parts = file_name.split('.');
      return parts[parts.length - 1];
    },
    isAllowedFileType(item) {
      const banned_file_types = [
        'mp4',
        'mov',
        'mov,mp4,m4a,3gp,3g2,mj2',
        'mkv',
        'avi',
        'swf',
      ];
      const file_name = item.name;
      const file_type = this.getFileExtension(file_name);
      const ffmpeg_file_type = item.ffmpeg_details.format_name;

      if (_.includes(banned_file_types, file_type) || _.includes(banned_file_types, ffmpeg_file_type)) {
        return false;
      }

      return true;
    },
    changeListView(value) {
      this.view = value;
    },
    itemWidthPercentage(index) {
      return this.calculatedItemRatios[index].percent;
    },
    addSilence() {
      this.$validator.validateAll('silence').then((result) => {
        if (!result) {
          toastr.error("Silence input is incorrectly formatted");
          return;
        }
      });

      this.silenceId = this.silenceId + 1;
      const item_id = `silence-${this.silenceId}`;
      const data = {
        id: item_id,
        name: `Silence ${this.silence}`,
        original_name: `Silence ${this.silence}`,
        silence: true,
        ffmpeg_details: {
          duration: parseInt(this.convertToSeconds(this.silence)),
        },
      };

      this.selected_assets.push(data);
      this.checkCompatibility();
    },
    convertToSeconds(time) {
      const [h, m, s] = time.split(`:`); // split it at the colons
      // minutes are worth 60 seconds. Hours are worth 60 minutes.
      return (+h * 60 * 60) + (+m * 60) + (+s);
    },
    addAsset(asset) {
      this.selected_assets.push({
        ...asset,
        silence: false,
      });
      this.checkCompatibility();
      toastr.success(`Asset(s) added.`);
    },
    removeItem(selected_asset) {
      // Remove the removed field from the selected array
      const i = this.selected_assets.findIndex(item => item.uuid === selected_asset.uuid);
      this.selected_assets.splice(i, 1);
      this.checkCompatibility();
    },

    checkCompatibility() {
      this.incompatibility_issues = [];
      this.bit_rate_incompatible = false;
      this.format_incompatible = false;
      const compatability_data = [];
      const formats = [];
      let silence_file_count = 0;

      // Make sure we have more than 1 file added to the stitching
      if (this.selected_assets.length <= 1) {
        return;
      }

      // Loop through selected storage assets and build an array of data we need to check
      _.forEach(this.selected_assets, (item) => {
        if (!item.silence) {
          const data_to_compare = {
            item_id: item.uuid,
            format_name: item.ffmpeg_details.format_name,
            bit_rate: item.ffmpeg_details.bitrate,
          };

          // Push format to an array so we can see how many formats there are
          formats.push(item.ffmpeg_details.format_name);
          compatability_data.push(data_to_compare);
        } else {
          silence_file_count++;
        }
      });

      if (silence_file_count === this.selected_assets.length) {
        return;
      }

      // Make the array unique so that we can tell if there are multiple formats
      const unique_formats = $.unique(formats);

      // If we have more than 1 format then it's incompatible
      if (unique_formats.length > 1) {
        this.incompatibility_issues.push(
          "Multiple file formats have been detected : If you proceed all files will be converted to mp3.",
        );

        this.format_incompatible = true;
      }

      // Order the array by its bitrate
      const ordered_by_bitrate = _.orderBy(compatability_data, "bit_rate", [
        "desc",
      ]);
      // Get the highest and lowest bitrates
      const first = _.first(ordered_by_bitrate);
      const last = _.last(ordered_by_bitrate);
      // Subtract the bitrates from eachother so we know how difference they are
      const difference = first.bit_rate - last.bit_rate;

      // Check to see if the bit rate difference is within toleration
      if (difference > this.max_bitrate_diff) {
        this.incompatibility_issues.push(
          `Multiple bit rates have been detected : The highest value is ${first.bit_rate} and the lowest is ${last.bit_rate}. If you proceed the output will be at ${last.bit_rate}`,
        );
        this.bit_rate_incompatible = true;
      }
    },

    stitch() {
      this.$validator.validateAll('file-name').then(() => {
        if (!this.stitch_name) {
          toastr.error("You must name your new file.");
          return;
        }

        const data = {
          storage_assets: this.selected_assets,
          name: this.stitch_name,
          bit_rate_incompatible: this.bit_rate_incompatible,
          format_incompatible: this.format_incompatible,
        };

        axios.post(`/api/v1/stitcher`, data).then(() => {
          toastr.success(
            "Your stitch is being processed. Once complete it will appear in your storage",
          );
        }).catch((error) => {
          window.handleAxiosError(error);
        });
      });
    },
  },
};
</script>

<style lang="scss">
.list {
    width: 100%;
    max-height: 80vh;
    margin: 20px auto 20px 0;
    padding: 0;
    overflow: auto;
    background-color: #f3f3f3;
    border: 1px solid #efefef;
    border-radius: 3;
}

.list-item {
    align-items: center;
    padding: 20px;
    background-color: #fff;
    border-bottom: 1px solid #efefef;
    box-sizing: border-box;
    user-select: none;
    color: #333;
}

.handle {
    display: block;
    float: left;
    width: 18px;
    height: 18px;
    background-image: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"><path d="M 0 7.5 L 0 12.5 L 50 12.5 L 50 7.5 L 0 7.5 z M 0 22.5 L 0 27.5 L 50 27.5 L 50 22.5 L 0 22.5 z M 0 37.5 L 0 42.5 L 50 42.5 L 50 37.5 L 0 37.5 z" color="black"></path></svg>');
    background-size: contain;
    background-repeat: no-repeat;
    opacity: 0.25;
    margin-right: 20px;
    cursor: row-resize;
}

.horizontal-list {
    width: 100%;
    white-space: nowrap;
    display: flex;
}

.item-title {
    margin: 10px 0;
    font-weight: bold;
    border-left: 1px solid #000;
    padding-left: 5px;
}

.item-title:first-child {
    border: none !important;
}

.horizontal-list-item {
    /*cursor: ew-resize;*/
    padding: 20px 0;
    background-color: #fff;
    box-sizing: border-box;
    color: #333;
    overflow-wrap: break-word;
    position: relative;

    .bar-actions {
        display: none;
        position: absolute;
        top: 0;
        right: 0;
        z-index:999;
        .handle {
            margin-right:10px;
            background-image:none;
            opacity:1;
            cursor: ew-resize;
        }
        .btn {
          height: 25px;
          width: 40px;
          padding: 0;
        }
    }

    &:hover {
        /*border: 2px dashed #ccc;*/
        word-wrap: break-word;
        white-space: normal;
        .item-title {
            word-wrap: break-word;
            p {
                word-wrap: break-word;
            }
        }

        .bar-actions {
            display:inline-block;

        }
    }

    .bar {
        width: 100%;
        height: 40px;
        background-image: url("/build/media/misc/waveform.png");
        background-repeat: repeat-x;
        background-position: center;
        background-size: auto 80%;
        -webkit-border-radius: 5px;
        -moz-border-radius: 5px;
        border-radius: 5px;
        border-color: #dae1e7;
       &.silence {
         background-image: none;
       }
    }

    &:nth-child(4n + 0) {
        .bar {
            background-color: rgba(150, 0, 0, 0.3); // red
        }
    }
    &:nth-child(4n + 1) {
        .bar {
            background-color: rgba(0, 0, 150, 0.3); // blue
        }
    }
    &:nth-child(4n + 2) {
        .bar {
            background-color: rgba(0, 150, 0, 0.3); // green
        }
    }
    &:nth-child(4n + 3) {
        .bar {
            background-color: rgba(255, 100, 0, 0.3); // orange
        }
    }
}

.stitcher-placeholder {
    text-align: center;
    border: 2px dashed #dae1e7;
    padding: 50px 0;
    margin: 20px 0;
}
</style>
